import { useEffect } from 'react';
import { updateSEO, getSiteUrl } from '../../utils/seo';

export default function ServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = getSiteUrl();
    
    updateSEO({
      title: '事業内容 | おかやま介護認定調査株式会社 - 介護認定調査・更新調査・変更調査',
      description: '岡山市の介護認定調査サービス。新規認定調査、更新調査、変更調査に対応。経験豊富な介護支援専門員が、一人ひとりの状況を丁寧に把握し、正確な調査を実施。24時間以内の迅速対応で岡山市全域をカバーします。',
      keywords: '事業内容,介護認定調査,更新調査,変更調査,訪問調査,岡山市,介護保険,要介護認定,サービス内容',
      canonical: `${siteUrl}/service`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Service',
            '@id': `${siteUrl}/service#service`,
            serviceType: '介護認定調査',
            provider: {
              '@id': `${siteUrl}/#organization`
            },
            areaServed: {
              '@type': 'City',
              name: '岡山市'
            },
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: '介護認定調査サービス',
              itemListElement: [
                {
                  '@type': 'Offer',
                  itemOffered: {
                    '@type': 'Service',
                    name: '介護認定調査',
                    description: '新規に介護保険サービスの利用を希望される方を対象とした認定調査。ご本人様の心身の状態や日常生活の様子を詳しくお伺いし、適切な要介護度の判定につながる調査を実施。'
                  }
                },
                {
                  '@type': 'Offer',
                  itemOffered: {
                    '@type': 'Service',
                    name: '更新調査',
                    description: '認定の有効期間満了に伴う更新時の調査。前回の認定時からの状態の変化を確認し、現在の状況に応じた適切な要介護度の判定をサポート。'
                  }
                },
                {
                  '@type': 'Offer',
                  itemOffered: {
                    '@type': 'Service',
                    name: '変更調査',
                    description: '認定期間中に心身の状態が大きく変化した場合の調査。迅速な対応により、現在の状態に応じた適切な介護サービスを受けられるようサポート。'
                  }
                }
              ]
            }
          },
          {
            '@type': 'WebPage',
            '@id': `${siteUrl}/service#webpage`,
            url: `${siteUrl}/service`,
            name: '事業内容 | おかやま介護認定調査株式会社',
            description: '介護認定調査、更新調査、変更調査など、当社が提供するサービス内容をご紹介します。',
            isPartOf: {
              '@id': `${siteUrl}/#website`
            }
          }
        ]
      }
    });
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-[#F5F1E8] to-[#E8F4F4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 shadow-sm">
            <div className="w-2 h-2 bg-[#5D9B9B] rounded-full"></div>
            <span className="text-sm font-medium text-[#2C3E50]">SERVICE</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C3E50] mb-6">
            事業内容
          </h1>
          <p className="text-lg sm:text-xl text-gray-700">
            介護認定調査の専門家として、質の高いサービスを提供します
          </p>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] text-center mb-16">
            提供サービス
          </h2>

          <div className="space-y-12">
            {/* Service 1 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 lg:p-12">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/39376ada57d8cb25cbfc78e9350cc316.jpeg"
                    alt="介護認定調査サービス"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <h3 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-6">
                    介護認定調査
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    新規に介護保険サービスの利用を希望される方を対象とした認定調査です。ご本人様の心身の状態や日常生活の様子を詳しくお伺いし、適切な要介護度の判定につながる調査を実施いたします。
                  </p>
                  <ul className="space-y-3">
                    {[
                      '有資格者による丁寧なヒアリング',
                      '心身の状態の詳細な確認',
                      '日常生活動作の評価',
                      '正確な調査票の作成'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#5D9B9B] text-xl"></i>
                        </div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 lg:p-12">
                <div className="flex flex-col justify-center order-2 lg:order-1">
                  <h3 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-6">
                    更新調査
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    認定の有効期間満了に伴う更新時の調査です。前回の認定時からの状態の変化を確認し、現在の状況に応じた適切な要介護度の判定をサポートいたします。
                  </p>
                  <ul className="space-y-3">
                    {[
                      '前回調査からの変化の確認',
                      '現在の介護サービス利用状況の把握',
                      '継続的なサポート体制',
                      'スムーズな更新手続きのサポート'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#5D9B9B] text-xl"></i>
                        </div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl overflow-hidden order-1 lg:order-2">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/63cb7c7367b37ae0a511461a1c01de2d.jpeg"
                    alt="更新調査サービス"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 lg:p-12">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/18a0f35fea0100e996f7052ad2a0ed8a.jpeg"
                    alt="変更調査サービス"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <h3 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-6">
                    変更調査
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    認定期間中に心身の状態が大きく変化した場合の調査です。迅速な対応により、現在の状態に応じた適切な介護サービスを受けられるようサポートいたします。
                  </p>
                  <ul className="space-y-3">
                    {[
                      '状態変化の詳細な確認',
                      '迅速な調査対応',
                      '医療機関との連携',
                      '適切なサービス変更のサポート'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#5D9B9B] text-xl"></i>
                        </div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] text-center mb-16">
            調査の流れ
          </h2>

          <div className="space-y-8">
            {[
              {
                step: '01',
                title: 'ご依頼・お問い合わせ',
                description: 'お電話またはお問い合わせフォームからご連絡ください。ご依頼内容を確認させていただきます。'
              },
              {
                step: '02',
                title: '日程調整',
                description: 'ご都合の良い日時を伺い、訪問調査の日程を調整いたします。24時間以内にご連絡いたします。'
              },
              {
                step: '03',
                title: '訪問調査',
                description: '調査員がご自宅を訪問し、心身の状態や日常生活の様子を丁寧にお伺いします。所要時間は1〜1.5時間程度です。'
              },
              {
                step: '04',
                title: '調査票作成',
                description: '訪問調査の内容をもとに、正確な調査票を作成いたします。専門知識を活かした適切な記載を心がけています。'
              },
              {
                step: '05',
                title: '提出・報告',
                description: '作成した調査票を市町村に提出し、ご依頼元にも報告いたします。その後の流れについてもご説明いたします。'
              }
            ].map((item, index) => (
              <div key={index}>
                <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-[#5D9B9B] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">STEP</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl font-bold text-[#5D9B9B]">{item.step}</span>
                        <h3 className="text-xl sm:text-2xl font-bold text-[#2C3E50]">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
                {index < 4 && (
                  <div className="flex justify-center py-4">
                    <i className="ri-arrow-down-line text-3xl text-[#5D9B9B]"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Area Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] text-center mb-12">
            対応エリア
          </h2>

          <div className="bg-[#F5F1E8] rounded-2xl p-8 sm:p-12 text-center">
            <div className="mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/31c5dbb1016316db37621f894435e3e3.jpg"
                alt="岡山市対応エリアマップ"
                className="w-full max-w-2xl mx-auto rounded-xl"
              />
            </div>
            <h3 className="text-2xl font-bold text-[#2C3E50] mb-6">
              岡山市全域に対応
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {['北区', '中区', '東区', '南区'].map((area, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg py-4 px-6 font-medium text-[#2C3E50] shadow-sm"
                >
                  岡山市{area}
                </div>
              ))}
            </div>
            <p className="text-gray-600 text-lg">
              その他の近隣地域もご相談ください。<br />
              お気軽にお問い合わせください。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
